import clsx from 'clsx';
import React from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';

import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import useEventTracking from '@hooks/useEventTracking';
import useVeriffSession from '@hooks/useVeriffSession';
import { ClickTarget, click } from '@tracking';
import { OfferOption, SelectedOffer } from '@utils/backend-api/subscribe';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/constants';

interface OfferProps {
  offer: OfferOption;
  selectedOfferCode: string;
  setSelectedOffer?: (plan: SelectedOffer) => void;
}

const subString = 'The Information';

const MobileOffer = ({
  offer: {
    billingFrequency,
    code,
    currentPrice,
    currentPriceDisplayOverride,
    description,
    isPro,
    isBestValue,
    originalPrice,
    shortDescription,
    title,
  },
  selectedOfferCode,
  setSelectedOffer,
}: OfferProps) => {
  const { trackEvent } = useEventTracking();
  const isSelected = selectedOfferCode === code;
  const currentPriceToDisplay = currentPriceDisplayOverride || currentPrice;
  const { startVeriffSession } = useVeriffSession();
  const {
    user: { dobVerified, isYPEligible },
  } = useSubscribeForm();

  const handleOfferSelection = () => {
    setSelectedOffer({
      description,
      billingFrequency,
      code,
      currentPrice,
      isPro,
      originalPrice,
      title,
    });
    trackEvent(click({ clickTarget: ClickTarget.MobileOffer, offerCode: code }));

    const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);
    if (isYPPlan && !isYPEligible && dobVerified) {
      toast.success(
        'You are not eligible for the Young Professionals plan. Please select another plan.'
      );
    } else if (isYPPlan && !dobVerified) {
      startVeriffSession();
    }
  };

  const offerTitle = (title: string) => {
    return title.replace(subString, '').trim();
  };

  return (
    <div
      id={code}
      onClick={handleOfferSelection}
      className="relative m-auto my-16 flex max-w-md cursor-pointer items-start first:mt-0 last:mb-0 md:max-w-full"
    >
      <div className="flex items-start">
        <div
          className={clsx(
            'mr-8 flex h-24 w-24 items-center justify-center rounded-full border border-solid p-4',
            {
              'border-pro-gold': isPro,
              'border-grayscale-gray': !isPro,
              'border-primary-magenta': isSelected && !isPro,
            }
          )}
        >
          <span
            className={clsx('h-full w-full rounded-full', {
              block: isSelected,
              hidden: !isSelected,
              'bg-pro-gold': isPro,
              'bg-primary-magenta': !isPro,
            })}
          />
        </div>
        <div className="w-[100px]">
          <h2
            className={clsx('m-0 font-suisse-intl text-caption-sm', {
              'text-pro-gold': isPro,
              'text-grayscale-gray': !isPro,
              'text-primary-magenta': isSelected && !isPro,
            })}
          >
            <span className="block font-bold">{offerTitle(title)}</span>
            <span className="block font-bold">${currentPriceToDisplay}</span>
            {isBestValue && <span className="block font-light">Best Value</span>}
          </h2>
        </div>
      </div>
      <p
        className={clsx('m-0 font-suisse-intl text-button-xs', {
          'text-black': isSelected,
          'text-grayscale-gray': !isSelected,
        })}
      >
        {ReactHtmlParser(shortDescription || description)}
      </p>
    </div>
  );
};

export default MobileOffer;
